import React, { useState, useEffect } from "react";
import { X, Bell, RefreshCcw, CheckCircle } from "lucide-react";
import {
  notificationService,
  Notification,
} from "../../services/NotificationService";
import NotificationItem from "./NotificationItem";
import { useToastContext } from "../../context/ToastContext";
import { useNotifications } from "../../context/NotificationContext";
import { notificationSocketService } from "../../services/NotificationSocketService";

const NotificationPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const { showToast } = useToastContext();
  const { notificationUnreadCount, fetchUnreadCount } = useNotifications();

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    notificationSocketService.onNewNotification = (notification: Notification) => {
      setNotifications((prev) => [notification, ...prev]);
      fetchUnreadCount();
    };

    notificationSocketService.onNotificationUpdated = ({
      notificationId,
      updates,
    }) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notificationId ? { ...n, ...updates } : n
        )
      );
    };

    notificationSocketService.onNotificationDeleted = ({ notificationId }) => {
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      fetchUnreadCount();
    };

    notificationSocketService.onNotificationsRead = ({ notificationIds }) => {
      setNotifications((prev) => {
        // If notificationIds is undefined or empty, mark all as read (mark all as read case)
        if (!notificationIds || !Array.isArray(notificationIds) || notificationIds.length === 0) {
          return prev.map((n) => ({ ...n, read: true }));
        }
        // Otherwise, only mark the specified notifications as read
        return prev.map((n) =>
          notificationIds.includes(n._id) ? { ...n, read: true } : n
        );
      });
      fetchUnreadCount();
    };

    notificationSocketService.onUnreadCountChanged = () => {
      fetchUnreadCount();
    };
  }, [fetchUnreadCount]);

  const fetchNotifications = async (reset = false) => {
    if (isRefreshing && !reset) return;

    setIsRefreshing(true);
    const currentOffset = reset ? 0 : offset;

    try {
      const { data } = await notificationService.getNotifications({
        offset: currentOffset,
        limit: 50,
      });

      if (reset) {
        setNotifications(data.notifications);
      } else {
        setNotifications((prev) => [...prev, ...data.notifications]);
      }

      setOffset(data.offset + data.limit);
      setHasMore(data.hasMore);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      showToast("error", "Failed to load notifications");
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
      setIsLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (isMarkingRead) return;

    setIsMarkingRead(true);

    try {
      const { message } = await notificationService.markAllNotificationsAsRead();
      showToast("success", message);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      fetchUnreadCount();
    } catch (error: any) {
      console.error("Failed to mark all as read:", error);
      const errorMessage =
        error?.message || "Failed to mark notifications as read.";
      showToast("error", errorMessage);
    } finally {
      setTimeout(() => setIsMarkingRead(false), 500);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  return (
    <div
      className={`absolute top-2 right-0 w-[400px] rounded-xl bg-background-paper dark:bg-background-panelDark shadow-2xl border border-divider dark:border-divider-dark transition-all duration-300 transform ${
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 scale-95"
      }`}
      style={{ overflow: "visible" }}
    >
      <div className="relative px-5 py-4 border-b border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark/90 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary dark:text-text-primaryDark" />
            <h3 className="text-lg font-semibold text-text-primary dark:text-white">
              Notifications
            </h3>
            {notificationUnreadCount && notificationUnreadCount.unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                {notificationUnreadCount.unreadCount}
              </span>
            )}
          </div>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition"
            onClick={onClose}
          >
            <X className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[450px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
        {isLoading ? (
          <div className="space-y-3 animate-pulse">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-200 dark:bg-gray-800 rounded-lg"
              >
                <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-md"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-3/4 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  <div className="w-1/2 h-3 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-text-secondary dark:text-text-secondaryDark uppercase mb-2">
                Recent
              </h4>
              <div className="space-y-2">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    refreshNotifications={() => fetchNotifications(true)}
                    onMarkAsRead={() => handleMarkAsRead(notification._id)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-40">
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark text-center">
              All clear! Your AI-powered RevOps engine <br /> is running
              smoothly.
            </p>
          </div>
        )}
      </div>

      <div className="relative px-5 py-3 bg-background-default dark:bg-background-dark/90 border-t border-divider dark:border-divider-dark rounded-b-xl">
        <div className="flex items-center justify-between">
          <button
            className={`flex items-center gap-2 text-sm font-medium transition
    ${
      isMarkingRead || (notificationUnreadCount?.unreadCount ?? 0) === 0
        ? "text-text-secondary dark:text-gray-500 cursor-not-allowed"
        : "text-text-primary dark:text-white hover:text-primary dark:hover:text-secondary"
    }`}
            onClick={markAllAsRead}
            disabled={
              isMarkingRead || (notificationUnreadCount?.unreadCount ?? 0) === 0
            }
          >
            <CheckCircle
              className={`w-4 h-4 ${
                isMarkingRead ? "animate-spin" : ""
              } transition-transform`}
            />
            Mark all as read
          </button>

          <button
            className="flex items-center gap-2 text-sm font-medium text-text-secondary dark:text-gray-400 hover:text-primary dark:hover:text-secondary transition"
            onClick={() => fetchNotifications(true)}
            disabled={isRefreshing}
          >
            <RefreshCcw
              className={`w-4 h-4 ${
                isRefreshing ? "animate-spin" : ""
              } transition-transform`}
            />
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
